import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import * as passwordApi from 'shared/api/auth/passwordApi';

const SecuritySettings = ({ onUnsavedChanges, hasUnsavedChanges }) => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loginNotifications, setLoginNotifications] = useState(true);
  const [securityQuestions, setSecurityQuestions] = useState([
    { question: 'Как звали вашего первого питомца?', answer: '' },
    { question: 'В каком городе вы родились?', answer: '' }
  ]);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handlePasswordChange = (field, value) => {
    const newForm = { ...passwordForm, [field]: value };
    setPasswordForm(newForm);
    
    if (field === 'newPassword') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    
    const hasChanges = newForm.currentPassword || newForm.newPassword || newForm.confirmPassword;
    onUnsavedChanges(hasChanges);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Слабый';
    if (passwordStrength < 50) return 'Средний';
    if (passwordStrength < 75) return 'Хороший';
    return 'Отличный';
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Пароли не совпадают');
      return;
    }
    
    if (passwordStrength < 50) {
      setPasswordError('Пароль слишком слабый. Используйте более сложный пароль.');
      return;
    }
    
    setIsChangingPassword(true);
    try {
      await passwordApi.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      setPasswordSuccess('Пароль успешно изменен. На ваш email отправлено уведомление.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordStrength(0);
      onUnsavedChanges(false);
    } catch (err) {
      console.error('Password change failed:', err);
      if (err.status === 400) {
        setPasswordError('Неверный текущий пароль');
      } else {
        setPasswordError(err.message || 'Не удалось изменить пароль');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleTwoFactorToggle = () => {
    if (!twoFactorEnabled) {
      // Show setup modal in real app
      alert('Настройка двухфакторной аутентификации будет доступна в следующей версии');
    } else {
      if (window.confirm('Вы уверены, что хотите отключить двухфакторную аутентификацию?')) {
        setTwoFactorEnabled(false);
      }
    }
  };

  const securityEvents = [
    {
      id: 1,
      type: 'login',
      description: 'Успешный вход в систему',
      location: 'Москва, Россия',
      device: 'Chrome на Windows',
      timestamp: '15 декабря 2024, 14:30',
      status: 'success'
    },
    {
      id: 2,
      type: 'password_change',
      description: 'Изменение пароля',
      location: 'Москва, Россия',
      device: 'Chrome на Windows',
      timestamp: '10 декабря 2024, 09:15',
      status: 'success'
    },
    {
      id: 3,
      type: 'failed_login',
      description: 'Неудачная попытка входа',
      location: 'Неизвестно',
      device: 'Неизвестно',
      timestamp: '8 декабря 2024, 22:45',
      status: 'warning'
    }
  ];

  const getEventIcon = (type) => {
    switch (type) {
      case 'login': return 'LogIn';
      case 'password_change': return 'Key';
      case 'failed_login': return 'AlertTriangle';
      default: return 'Activity';
    }
  };

  const getEventColor = (status) => {
    switch (status) {
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-secondary-500';
    }
  };

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">
          Настройки безопасности
        </h2>
        <p className="text-text-secondary mt-1">
          Управляйте паролем, двухфакторной аутентификацией и другими настройками безопасности
        </p>
      </div>

      {/* Password Change Section */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
          <Icon name="Key" size={20} className="text-primary" />
          Изменение пароля
        </h3>
        
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Текущий пароль *
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                className="
                  text-text-primary bg-secondary-50
                  w-full px-3 py-2 pr-10 border border-secondary-300 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                  transition-colors duration-150
                "
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
              >
                <Icon name={showPasswords.current ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Новый пароль *
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                className="
                  text-text-primary bg-secondary-50
                  w-full px-3 py-2 pr-10 border border-secondary-300 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                  transition-colors duration-150
                "
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
              >
                <Icon name={showPasswords.new ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>
            
            {passwordForm.newPassword && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-1 bg-secondary-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                      style={{ width: `${passwordStrength}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-text-secondary">
                    {getPasswordStrengthText()}
                  </span>
                </div>
                <div className="text-xs text-text-secondary space-y-1">
                  <p>Требования к паролю:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li className={passwordForm.newPassword.length >= 8 ? 'text-success' : 'text-error'}>
                      Минимум 8 символов
                    </li>
                    <li className={/[A-Z]/.test(passwordForm.newPassword) ? 'text-success' : 'text-error'}>
                      Заглавные буквы
                    </li>
                    <li className={/[0-9]/.test(passwordForm.newPassword) ? 'text-success' : 'text-error'}>
                      Цифры
                    </li>
                    <li className={/[^A-Za-z0-9]/.test(passwordForm.newPassword) ? 'text-success' : 'text-error'}>
                      Специальные символы
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Подтвердите новый пароль *
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                className="
                  text-text-primary bg-secondary-50
                  w-full px-3 py-2 pr-10 border border-secondary-300 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                  transition-colors duration-150
                "
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
              >
                <Icon name={showPasswords.confirm ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>
            {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
              <p className="text-error text-sm mt-1">Пароли не совпадают</p>
            )}
          </div>

          <button
            type="submit"
            className="
              px-4 py-2 bg-primary text-white rounded-md
              hover:bg-primary-700 transition-colors duration-150 hover-lift
              focus:outline-none focus:ring-2 focus:ring-primary/20
              flex items-center gap-2
            "
          >
            <Icon name="Save" size={16} />
            Изменить пароль
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
              <Icon name="Shield" size={20} className="text-primary" />
              Двухфакторная аутентификация
            </h3>
            <p className="text-text-secondary text-sm mt-1">
              Дополнительный уровень защиты для вашего аккаунта
            </p>
          </div>
          <button
            onClick={handleTwoFactorToggle}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-150
              focus:outline-none focus:ring-2 focus:ring-primary/20
              ${twoFactorEnabled ? 'bg-primary' : 'bg-secondary-300'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-150
                ${twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
        
        {twoFactorEnabled ? (
          <div className="flex items-center gap-2 text-success">
            <Icon name="CheckCircle" size={16} />
            <span className="text-sm font-medium">Двухфакторная аутентификация включена</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-warning">
            <Icon name="AlertCircle" size={16} />
            <span className="text-sm font-medium">Двухфакторная аутентификация отключена</span>
          </div>
        )}
      </div>

      {/* Login Notifications */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
              <Icon name="Bell" size={20} className="text-primary" />
              Уведомления о входе
            </h3>
            <p className="text-text-secondary text-sm mt-1">
              Получать уведомления о входе в аккаунт на email
            </p>
          </div>
          <button
            onClick={() => setLoginNotifications(!loginNotifications)}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-150
              focus:outline-none focus:ring-2 focus:ring-primary/20
              ${loginNotifications ? 'bg-primary' : 'bg-secondary-300'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-150
                ${loginNotifications ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>

      {/* Security Activity */}
      <div>
        <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-primary" />
          Последняя активность
        </h3>
        
        <div className="bg-surface border border-secondary-200 rounded-lg divide-y divide-secondary-100">
          {securityEvents.map(event => (
            <div key={event.id} className="p-4">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-md bg-secondary-100 ${getEventColor(event.status)}`}>
                  <Icon name={getEventIcon(event.type)} size={16} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-text-primary">{event.description}</p>
                  <div className="text-sm text-text-secondary mt-1 space-y-1">
                    <p>Устройство: {event.device}</p>
                    <p>Местоположение: {event.location}</p>
                    <p>Время: {event.timestamp}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;